/**
 * Design-system tokens for themed form inputs.
 *
 * Usage:
 *   import { INPUT_THEMES, INPUT_SIZES, type InputTheme, type InputSize } from '@/components/ui/form/inputThemes';
 *   const t = INPUT_THEMES[theme ?? 'dark'];
 *   const s = INPUT_SIZES[size ?? 'md'];
 *   <input className={`${s.base} ${t.input}`} />
 */

/**
 * Raw CSS values for properties that conflict with Shadcn's SelectTrigger defaults.
 * Use these as inline styles (style={}) to guarantee they always win over Tailwind classes,
 * since Tailwind resolves class conflicts by CSS file order — not className order.
 */
export const INPUT_STYLE_TOKENS = {
  dark: {
    borderColor: 'rgba(255,255,255,0.15)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: '#FFFFFF',
    /** Explicit font size as inline style — prevents Shadcn/Tailwind class conflicts.
     *  text-sm = 14px. Using inline style guarantees this value wins over any class. */
    fontSize: '14px',
    /** Tailwind class to color the chevron SVG inside the trigger */
    chevronClass: '[&_svg]:text-white/50',
  },
  light: {
    borderColor: '#D1D5DB',
    backgroundColor: '#F9FAFB',
    color: '#111827',
    fontSize: '14px',
    chevronClass: '[&_svg]:text-gray-400',
  },
} as const;

/** @deprecated Use INPUT_STYLE_TOKENS for Select components. Kept for other usage. */
export const INPUT_TOKENS = {
  dark: {
    border: 'border-[rgba(255,255,255,0.15)]',
    bg: 'bg-[rgba(255,255,255,0.06)]',
    text: 'text-white',
    placeholder: 'placeholder:text-white/80',
    ring: 'focus-visible:ring-red-700',
  },
  light: {
    border: 'border-gray-300',
    bg: 'bg-gray-50',
    text: 'text-gray-900',
    placeholder: 'placeholder:text-gray-500',
    ring: 'focus-visible:ring-red-600',
  },
} as const;

export const INPUT_THEMES = {
  dark: {
    /** Base classes for <input> and autocomplete fields */
    input:
      'h-11 rounded-lg border font-sans text-white ' +
      'placeholder:text-white/80 ' +
      'bg-[rgba(255,255,255,0.06)] border-[rgba(255,255,255,0.15)] ' +
      'focus:outline-none focus-visible:ring-1 focus-visible:ring-red-700 focus-visible:ring-offset-0',

    /** Base classes for <select> triggers (after Shadcn SelectTrigger was cleaned) */
    select:
      'text-sm text-white ' +
      'bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.15)] ' +
      'focus:outline-none focus-visible:ring-1 focus-visible:ring-red-700 focus-visible:ring-offset-0',

    /** Dropdown container */
    dropdown: 'bg-[#1E1212] border border-white/10',

    /** Each dropdown option row */
    dropdownItem: 'text-white hover:bg-white/10 cursor-pointer',

    /** Text inside a dropdown option */
    dropdownItemText: 'text-white text-sm font-sans',

    /** "Loading…" / "No results" helper text */
    helperText: 'text-white/60 text-sm',
  },

  light: {
    input:
      'h-11 rounded-lg border text-sm font-sans text-gray-900 ' +
      'placeholder:text-gray-500 ' +
      'bg-gray-50 border-gray-300 ' +
      'focus:outline-none focus-visible:ring-1 focus-visible:ring-red-600 focus-visible:ring-offset-0',

    select:
      'text-sm text-gray-900 ' +
      'bg-gray-50 border border-gray-300 ' +
      'focus:outline-none focus-visible:ring-1 focus-visible:ring-red-600 focus-visible:ring-offset-0',

    dropdown: 'bg-white border border-gray-200',
    dropdownItem: 'text-gray-900 hover:bg-gray-100 cursor-pointer',
    dropdownItemText: 'text-gray-900 text-sm font-sans',
    helperText: 'text-gray-500 text-sm',
  },
} as const;

export type InputTheme = keyof typeof INPUT_THEMES;

/** Size variants for inputs */
export const INPUT_SIZES = {
  sm: {
    base: 'h-8 text-xs px-3',
    padInput: 'px-3',
  },
  md: {
    base: 'h-11 text-sm px-4',  // default
    padInput: 'px-4',
  },
  lg: {
    base: 'h-13 text-base px-5',
    padInput: 'px-5',
  },
} as const;

export type InputSize = keyof typeof INPUT_SIZES;
